import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  CircularProgress, Alert, Snackbar, Divider, Chip, IconButton, Tooltip,
  Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem,
  Checkbox, FormGroup,
} from '@mui/material';
import {
  Save as SaveIcon, Refresh as RefreshIcon,
  Business as ClinicIcon, Person as PersonIcon,
  LocationOn as LocationIcon, Phone as PhoneIcon,
  Description as DocIcon, Palette as PaletteIcon,
  Settings as SettingsIcon,   Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Image as ImageIcon,
  DeleteOutline as DeleteOutlineIcon,
  PsychologyOutlined as PsychologyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { CLINIC_MEDICAL_SPECIALTY_OPTIONS } from '../../constants/clinicMedicalSpecialties';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const COMMON_TIMEZONES = [
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Bahia',
  'America/Belem',
  'America/Cuiaba',
  'America/Manaus',
  'America/Rio_Branco',
  'America/Noronha',
  'Atlantic/Azores',
  'Europe/Lisbon',
  'UTC',
];

interface IaSilenceRow {
  uid: string;
  weekdays: number[];
  start: string;
  end: string;
}

function silenceRowsFromApi(raw: unknown): { enabled: boolean; rows: IaSilenceRow[] } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { enabled: false, rows: [] };
  }
  const o = raw as Record<string, unknown>;
  const enabled = Boolean(o.enabled);
  const ws = Array.isArray(o.windows) ? o.windows : [];
  const rows: IaSilenceRow[] = [];
  ws.forEach((w: unknown, i: number) => {
    if (!w || typeof w !== 'object' || Array.isArray(w)) return;
    const x = w as Record<string, unknown>;
    const days = Array.isArray(x.weekdays)
      ? Array.from(
          new Set(
            (x.weekdays as unknown[])
              .map((d) => Number(d))
              .filter((d) => Number.isFinite(d) && d >= 0 && d <= 6),
          ),
        ).sort((a, b) => a - b)
      : [];
    rows.push({
      uid: `${Date.now()}_${i}_${Math.random().toString(36).slice(2, 7)}`,
      weekdays: days,
      start: typeof x.start === 'string' ? x.start : '08:00',
      end: typeof x.end === 'string' ? x.end : '18:00',
    });
  });
  return { enabled, rows };
}


const ClinicSettings: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  const logoInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [settings, setSettings] = useState({
    clinic_name: '', clinic_subtitle: '', cnpj: '', state_registration: '',
    municipal_registration: '', cnes: '',
    clinic_medical_specialty: '',
    responsible_name: '', responsible_registration: '', responsible_registration_type: 'CRM',
    responsible_registration_state: '', responsible_specialty: '',
    address_street: '', address_number: '', address_complement: '', address_neighborhood: '',
    address_city: '', address_state: '', address_zip: '',
    phone: '', phone_secondary: '', whatsapp: '', email: '', website: '',
    logo_url: '', primary_color: '#14B8A6', secondary_color: '#0D9488',
    budget_header_text: '', budget_footer_text: '', budget_terms_text: '',
    budget_warranty_text: '',
    default_budget_validity_days: 30, budget_numbering_prefix: 'ORC',
    nfse_enabled: false, nfse_natureza_operacao: '1', nfse_regime_tributacao: '',
    nfse_optante_simples: false, nfse_incentivador_cultural: false,
    nfse_codigo_municipio: '', nfse_item_lista_servico: '', nfse_codigo_tributario_municipio: '',
    nfse_codigo_cnae: '', nfse_aliquota_iss: 2.00, nfse_iss_retido: false,
    nfse_usar_nacional: false, nfse_codigo_tributacao_nacional: '', nfse_codigo_municipio_prestacao: '',
    agenda_display_first_hour: 7,
    agenda_display_last_hour: 20,
    whatsapp_ia_n8n_forward_enabled: true,
    whatsapp_ia_persistent_memory_enabled: false,
    whatsapp_ia_timezone: 'America/Sao_Paulo',
  });

  const [iaSilenceEnabled, setIaSilenceEnabled] = useState(false);
  const [iaSilenceRows, setIaSilenceRows] = useState<IaSilenceRow[]>([]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(getApiUrl('/clinic/settings'));
      if (res.data?.data) {
        const d = res.data.data;
        setSettings((prev) => ({
          ...prev,
          ...d,
          clinic_medical_specialty:
            typeof d.clinic_medical_specialty === 'string' ? d.clinic_medical_specialty : '',
          whatsapp_ia_n8n_forward_enabled: d.whatsapp_ia_n8n_forward_enabled !== false,
          whatsapp_ia_persistent_memory_enabled: Boolean(d.whatsapp_ia_persistent_memory_enabled),
          whatsapp_ia_timezone: (d.whatsapp_ia_timezone as string)?.trim() || 'America/Sao_Paulo',
        }));
        const parsed = silenceRowsFromApi(d.whatsapp_ia_silence_schedule);
        setIaSilenceEnabled(parsed.enabled && parsed.rows.length > 0);
        setIaSilenceRows(parsed.rows);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const schedulePayload =
        iaSilenceEnabled && iaSilenceRows.some((r) => r.weekdays.length > 0 && r.start.trim() && r.end.trim())
          ? {
              enabled: true,
              windows: iaSilenceRows
                .filter((r) => r.weekdays.length > 0 && r.start.trim() && r.end.trim())
                .map(({ weekdays, start, end }) => ({
                  weekdays: [...weekdays].sort((a, b) => a - b),
                  start: start.trim(),
                  end: end.trim(),
                })),
            }
          : { enabled: false, windows: [] as { weekdays: number[]; start: string; end: string }[] };

      await axios.put(getApiUrl('/clinic/settings'), {
        ...settings,
        whatsapp_ia_silence_schedule: schedulePayload,
      });
      setSnackbar({ open: true, message: 'Configurações salvas com sucesso!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar configurações', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: any) => setSettings(prev => ({ ...prev, [field]: value }));

  const toggleSilenceWeekday = (uid: string, day: number) => {
    setIaSilenceRows((rows) =>
      rows.map((r) => {
        if (r.uid !== uid) return r;
        const has = r.weekdays.includes(day);
        return {
          ...r,
          weekdays: has ? r.weekdays.filter((d) => d !== day).sort((a, b) => a - b) : [...r.weekdays, day].sort((a, b) => a - b),
        };
      }),
    );
  };

  const addSilenceWindow = () => {
    setIaSilenceRows((rows) => [
      ...rows,
      {
        uid: `nw_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        weekdays: [1, 2, 3, 4, 5],
        start: '08:00',
        end: '18:00',
      },
    ]);
    setIaSilenceEnabled(true);
  };

  const removeSilenceWindow = (uid: string) => {
    setIaSilenceRows((rows) => rows.filter((r) => r.uid !== uid));
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png' || !file.name.toLowerCase().endsWith('.png')) {
      setSnackbar({ open: true, message: 'Use apenas PNG (fundo transparente recomendado para o logo).', severity: 'error' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'O PNG deve ter no máximo 2 MB.', severity: 'error' });
      return;
    }
    try {
      setLogoUploading(true);
      const fd = new FormData();
      fd.append('logo', file);
      const res = await axios.post(getApiUrl('/clinic/settings/logo'), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.logo_url as string | undefined;
      if (url) {
        setSettings(prev => ({ ...prev, logo_url: url }));
        setSnackbar({ open: true, message: 'Logo enviada com sucesso.', severity: 'success' });
      } else {
        throw new Error('Resposta inválida');
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Erro ao enviar logo',
        severity: 'error',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      setLogoUploading(true);
      await axios.put(getApiUrl('/clinic/settings'), { ...settings, logo_url: '' });
      setSettings(prev => ({ ...prev, logo_url: '' }));
      setSnackbar({ open: true, message: 'Logo removida.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao remover logo', severity: 'error' });
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box>;
  }

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000000', fontSize: { xs: '1.5rem', md: '2.125rem' }, mb: 0.5 }}>
            Configurações da Clínica
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
            Defina as informações da clínica que aparecem nos orçamentos e documentos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchSettings} sx={{ bgcolor: 'rgba(20,184,166,0.1)', '&:hover': { bgcolor: 'rgba(20,184,166,0.2)' } }}>
              <RefreshIcon sx={{ color: '#0d9488' }} />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Salvar Configurações
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Dados da Clínica */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ClinicIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Dados da Clínica</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Nome da Clínica *" value={settings.clinic_name} onChange={(e) => update('clinic_name', e.target.value)} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Subtítulo / Especialidade" value={settings.clinic_subtitle} onChange={(e) => update('clinic_subtitle', e.target.value)} placeholder="Ex: Clínica de Especialidades Médicas e Odontológicas" /></Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="clinic_medical_specialty_label">Especialidade médica da clínica</InputLabel>
                    <Select
                      labelId="clinic_medical_specialty_label"
                      label="Especialidade médica da clínica"
                      value={settings.clinic_medical_specialty ?? ''}
                      onChange={(e) => update('clinic_medical_specialty', e.target.value)}
                    >
                      {CLINIC_MEDICAL_SPECIALTY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value === '' ? '_none' : opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ color: '#64748b', mt: 0.75, display: 'block' }}>
                    Define o perfil predominante para padronizar documentos e comportamentos do sistema nesta clínica.
                  </Typography>
                </Grid>
                <Grid item xs={6}><TextField fullWidth label="CNPJ" value={settings.cnpj} onChange={(e) => update('cnpj', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="CNES" value={settings.cnes} onChange={(e) => update('cnes', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Inscrição Estadual" value={settings.state_registration} onChange={(e) => update('state_registration', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Inscrição Municipal" value={settings.municipal_registration} onChange={(e) => update('municipal_registration', e.target.value)} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Responsável Técnico */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Responsável Técnico</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}><TextField fullWidth label="Nome do Responsável" value={settings.responsible_name} onChange={(e) => update('responsible_name', e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Tipo Registro" value={settings.responsible_registration_type} onChange={(e) => update('responsible_registration_type', e.target.value)} placeholder="CRM, CRO" /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Nº Registro" value={settings.responsible_registration} onChange={(e) => update('responsible_registration', e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="UF" value={settings.responsible_registration_state} onChange={(e) => update('responsible_registration_state', e.target.value)} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Especialidade" value={settings.responsible_specialty} onChange={(e) => update('responsible_specialty', e.target.value)} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Endereço */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Endereço</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={8}><TextField fullWidth label="Rua" value={settings.address_street} onChange={(e) => update('address_street', e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="Número" value={settings.address_number} onChange={(e) => update('address_number', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Complemento" value={settings.address_complement} onChange={(e) => update('address_complement', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Bairro" value={settings.address_neighborhood} onChange={(e) => update('address_neighborhood', e.target.value)} /></Grid>
                <Grid item xs={5}><TextField fullWidth label="Cidade" value={settings.address_city} onChange={(e) => update('address_city', e.target.value)} /></Grid>
                <Grid item xs={3}><TextField fullWidth label="Estado" value={settings.address_state} onChange={(e) => update('address_state', e.target.value)} /></Grid>
                <Grid item xs={4}><TextField fullWidth label="CEP" value={settings.address_zip} onChange={(e) => update('address_zip', e.target.value)} /></Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Contato */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PhoneIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Contato</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}><TextField fullWidth label="Telefone" value={settings.phone} onChange={(e) => update('phone', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Telefone Secundário" value={settings.phone_secondary} onChange={(e) => update('phone_secondary', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="WhatsApp" value={settings.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} /></Grid>
                <Grid item xs={6}><TextField fullWidth label="Email" value={settings.email} onChange={(e) => update('email', e.target.value)} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Website" value={settings.website} onChange={(e) => update('website', e.target.value)} /></Grid>
                <Grid item xs={12}>
                  <input ref={logoInputRef} type="file" accept="image/png,.png" hidden onChange={handleLogoFileChange} />
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                    Logo nos documentos (PNG, fundo transparente)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        width: 168,
                        height: 72,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        backgroundImage: 'linear-gradient(45deg, #f1f5f9 25%, transparent 25%), linear-gradient(-45deg, #f1f5f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f5f9 75%), linear-gradient(-45deg, transparent 75%, #f1f5f9 75%)',
                        backgroundSize: '12px 12px',
                        backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        p: 1,
                      }}
                    >
                      {settings.logo_url ? (
                        <Box component="img" src={settings.logo_url} alt="" sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', objectPosition: 'left center' }} />
                      ) : (
                        <ImageIcon sx={{ color: '#cbd5e1', fontSize: 32 }} />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 180 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                        Apenas PNG, até 2 MB. O arquivo aparece no canto superior esquerdo dos documentos impressos e em PDF.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={logoUploading}
                          onClick={() => logoInputRef.current?.click()}
                          sx={{ textTransform: 'none', borderColor: '#14b8a6', color: '#0d9488' }}
                        >
                          {logoUploading ? 'Enviando…' : 'Enviar PNG'}
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          color="error"
                          disabled={logoUploading || !settings.logo_url}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={removeLogo}
                          sx={{ textTransform: 'none' }}
                        >
                          Remover
                        </Button>
                      </Box>
                      <TextField
                        size="small"
                        fullWidth
                        label="Ou informe URL HTTPS (avançado)"
                        value={settings.logo_url}
                        onChange={(e) => update('logo_url', e.target.value)}
                        placeholder="https://..."
                        helperText="Opcional. Depois clique em Salvar Configurações para aplicar só a URL."
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Grade da agenda */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ScheduleIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Agenda — horários na grade</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Define qual intervalo do dia aparece nas vistas dia e semana. Fora deste período não são mostradas faixas nem cliques para novo agendamento; compromissos que ultrapassarem a janela são cortados na visualização.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Primeira hora</InputLabel>
                    <Select
                      label="Primeira hora"
                      value={settings.agenda_display_first_hour}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setSettings((prev) => {
                          const next = { ...prev, agenda_display_first_hour: v };
                          if (next.agenda_display_last_hour < v) next.agenda_display_last_hour = v;
                          return next;
                        });
                      }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Última hora</InputLabel>
                    <Select
                      label="Última hora"
                      value={settings.agenda_display_last_hour}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setSettings((prev) => {
                          const next = { ...prev, agenda_display_last_hour: v };
                          if (next.agenda_display_first_hour > v) next.agenda_display_first_hour = v;
                          return next;
                        });
                      }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1.5 }}>
                Padrão: 07:00 às 20:00 (a última linha da grade é a hora 20).
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* WhatsApp — IA automatizada */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <PsychologyIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>WhatsApp — assistente automatizado</Typography>
                <Chip
                  label={settings.whatsapp_ia_n8n_forward_enabled !== false ? 'Encaminhamento ativo' : 'Encaminhamento desligado'}
                  size="small"
                  sx={{
                    ml: { xs: 0, sm: 1 },
                    bgcolor: settings.whatsapp_ia_n8n_forward_enabled !== false ? '#dcfce7' : '#fef2f2',
                    color: settings.whatsapp_ia_n8n_forward_enabled !== false ? '#16a34a' : '#dc2626',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Controla o envio de mensagens <strong>recebidas</strong> do WhatsApp para a IA automatizada da clínica
                (Z-API, Cloud API, Evolution, 360). Útil para desligar a IA em horário de atendimento humano ou por completo.
              </Typography>
              <FormControlLabel
                control={(
                  <Switch
                    checked={settings.whatsapp_ia_n8n_forward_enabled !== false}
                    onChange={(e) => update('whatsapp_ia_n8n_forward_enabled', e.target.checked)}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }}
                  />
                )}
                label="Encaminhar mensagens recebidas para a IA automatizada"
              />
              <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: -0.5, mb: 2, pl: 0.5 }}>
                Desligue para que nenhum canal WhatsApp dispare respostas automáticas pela IA. O chat na plataforma continua normal.
              </Typography>

              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(settings.whatsapp_ia_persistent_memory_enabled)}
                    onChange={(e) => update('whatsapp_ia_persistent_memory_enabled', e.target.checked)}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }}
                  />
                )}
                label="Memória persistente da IA"
              />
              <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: -0.5, mb: 2, pl: 0.5 }}>
                Quando ligado, as mensagens trocadas no inbox com o paciente (ele e o atendente, sem usar a IA no fluxo normal) são enviadas ao mesmo webhook configurado para a IA, indicando quem enviou cada mensagem — para manter histórico e contexto quando o assistente for ativado.
              </Typography>

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fuso horário (IA)</InputLabel>
                    <Select
                      label="Fuso horário (IA)"
                      value={COMMON_TIMEZONES.includes(settings.whatsapp_ia_timezone || '')
                        ? settings.whatsapp_ia_timezone
                        : '__other__'}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        if (v === '__other__') return;
                        update('whatsapp_ia_timezone', v);
                      }}
                    >
                      {COMMON_TIMEZONES.map((tz) => (
                        <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                      ))}
                      <MenuItem value="__other__">Outro (editar abaixo)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IANA timezone (ex.: America/Cuiaba)"
                    value={settings.whatsapp_ia_timezone || 'America/Sao_Paulo'}
                    onChange={(e) => update('whatsapp_ia_timezone', e.target.value)}
                    helperText="Usado para calcular os horários sem IA."
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={(
                  <Switch
                    checked={iaSilenceEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setIaSilenceEnabled(on);
                      if (on && iaSilenceRows.length === 0) {
                        addSilenceWindow();
                      }
                    }}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }}
                  />
                )}
                label="Horários sem IA (silêncio programado)"
              />
              <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                Dentro destes períodos o sistema <strong>não</strong> delega mensagens à IA automatizada.
                Ex.: dias úteis 08:00–18:00 → a IA responde à noite e nos fins de semana; ajuste os dias e horários como quiser.
              </Typography>

              {iaSilenceEnabled && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {iaSilenceRows.map((row) => (
                    <Box
                      key={row.uid}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: '#f8fafc',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>Período sem IA automatizada</Typography>
                        <Button size="small" color="error" variant="text" startIcon={<DeleteOutlineIcon />} onClick={() => removeSilenceWindow(row.uid)} sx={{ textTransform: 'none' }}>
                          Remover
                        </Button>
                      </Box>
                      <FormGroup row sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {WEEKDAY_LABELS.map((label, day) => (
                          <FormControlLabel
                            key={day}
                            control={(
                              <Checkbox
                                size="small"
                                checked={row.weekdays.includes(day)}
                                onChange={() => toggleSilenceWeekday(row.uid, day)}
                                sx={{ py: 0 }}
                              />
                            )}
                            label={<Typography variant="caption">{label}</Typography>}
                            sx={{ mr: 1 }}
                          />
                        ))}
                      </FormGroup>
                      <Grid container spacing={1.5}>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Início"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={row.start}
                            onChange={(e) => setIaSilenceRows((rows) => rows.map((r) => (r.uid === row.uid ? { ...r, start: e.target.value } : r)))}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Fim"
                            type="time"
                            InputLabelProps={{ shrink: true }}
                            value={row.end}
                            onChange={(e) => setIaSilenceRows((rows) => rows.map((r) => (r.uid === row.uid ? { ...r, end: e.target.value } : r)))}
                          />
                        </Grid>
                      </Grid>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                        Intervalo que atravessa meia-noite: use duas janelas (ex. 22:00–23:59 e 00:00–06:00).
                      </Typography>
                    </Box>
                  ))}
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addSilenceWindow} sx={{ textTransform: 'none', alignSelf: 'flex-start', borderColor: '#14b8a6', color: '#0d9488' }}>
                    Adicionar período
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Textos do Orçamento */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DocIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Textos do Orçamento</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Texto do Cabeçalho (opcional)" value={settings.budget_header_text}
                    onChange={(e) => update('budget_header_text', e.target.value)} multiline rows={2}
                    placeholder="Texto adicional que aparece no cabeçalho do orçamento" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Texto do Rodapé" value={settings.budget_footer_text}
                    onChange={(e) => update('budget_footer_text', e.target.value)} multiline rows={2} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Termos e Condições" value={settings.budget_terms_text}
                    onChange={(e) => update('budget_terms_text', e.target.value)} multiline rows={3}
                    helperText="Texto que aparece nos termos e condições dos orçamentos" />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Texto de Garantia" value={settings.budget_warranty_text}
                    onChange={(e) => update('budget_warranty_text', e.target.value)} multiline rows={2}
                    placeholder="Descreva as condições de garantia (aparece quando habilitado no orçamento)" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Configurações Padrão */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SettingsIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Configurações de Numeração</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Prefixo do Orçamento" value={settings.budget_numbering_prefix}
                    onChange={(e) => update('budget_numbering_prefix', e.target.value)}
                    helperText="Ex: ORC, ORCM, ORCD" />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Validade Padrão (dias)" type="number" value={settings.default_budget_validity_days}
                    onChange={(e) => update('default_budget_validity_days', Number(e.target.value))} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Cores */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PaletteIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Cores do Orçamento</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField fullWidth label="Cor Primária" type="color" value={settings.primary_color}
                    onChange={(e) => update('primary_color', e.target.value)}
                    InputProps={{ sx: { height: 56 } }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth label="Cor Secundária" type="color" value={settings.secondary_color}
                    onChange={(e) => update('secondary_color', e.target.value)}
                    InputProps={{ sx: { height: 56 } }} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* Configurações Fiscais NFSe */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptIcon sx={{ color: '#14b8a6' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Configurações Fiscais (NFSe)</Typography>
                <Chip label={(settings as any).nfse_enabled ? 'Habilitado' : 'Desabilitado'}
                  size="small" sx={{ ml: 1, bgcolor: (settings as any).nfse_enabled ? '#dcfce7' : '#fef2f2',
                  color: (settings as any).nfse_enabled ? '#16a34a' : '#dc2626' }} />
              </Box>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Configure os dados fiscais para emissão de Nota Fiscal de Serviço Eletrônica via FocusNFe
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={(settings as any).nfse_enabled || false}
                      onChange={(e) => update('nfse_enabled', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }} />}
                    label="Habilitar emissão de NFSe" />
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Natureza da Operação</InputLabel>
                    <Select value={(settings as any).nfse_natureza_operacao || '1'} label="Natureza da Operação"
                      onChange={(e) => update('nfse_natureza_operacao', e.target.value)}>
                      <MenuItem value="1">1 - Tributação no município</MenuItem>
                      <MenuItem value="2">2 - Tributação fora do município</MenuItem>
                      <MenuItem value="3">3 - Isenção</MenuItem>
                      <MenuItem value="4">4 - Imune</MenuItem>
                      <MenuItem value="5">5 - Exig. suspensa - judicial</MenuItem>
                      <MenuItem value="6">6 - Exig. suspensa - administrativa</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Regime de Tributação</InputLabel>
                    <Select value={(settings as any).nfse_regime_tributacao || ''} label="Regime de Tributação"
                      onChange={(e) => update('nfse_regime_tributacao', e.target.value)}>
                      <MenuItem value="">Não informado</MenuItem>
                      <MenuItem value="1">1 - Microempresa municipal</MenuItem>
                      <MenuItem value="2">2 - Estimativa</MenuItem>
                      <MenuItem value="3">3 - Sociedade de profissionais</MenuItem>
                      <MenuItem value="4">4 - Cooperativa</MenuItem>
                      <MenuItem value="5">5 - MEI - Simples Nacional</MenuItem>
                      <MenuItem value="6">6 - ME/EPP - Simples Nacional</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={(settings as any).nfse_optante_simples || false}
                      onChange={(e) => update('nfse_optante_simples', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }} />}
                    label="Optante Simples Nacional" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={(settings as any).nfse_incentivador_cultural || false}
                      onChange={(e) => update('nfse_incentivador_cultural', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }} />}
                    label="Incentivador Cultural" />
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Código Município IBGE" value={(settings as any).nfse_codigo_municipio || ''}
                    onChange={(e) => update('nfse_codigo_municipio', e.target.value)}
                    helperText="7 dígitos (ex: 3550308 = São Paulo)" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Item Lista Serviço" value={(settings as any).nfse_item_lista_servico || ''}
                    onChange={(e) => update('nfse_item_lista_servico', e.target.value)}
                    helperText="LC 116/03 (ex: 0501 = medicina)" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Código Tributário Municipal" value={(settings as any).nfse_codigo_tributario_municipio || ''}
                    onChange={(e) => update('nfse_codigo_tributario_municipio', e.target.value)}
                    helperText="Varia por município" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="CNAE" value={(settings as any).nfse_codigo_cnae || ''}
                    onChange={(e) => update('nfse_codigo_cnae', e.target.value)}
                    helperText="7 dígitos (ex: 8630503)" />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField fullWidth size="small" label="Alíquota ISS (%)" type="number" value={(settings as any).nfse_aliquota_iss ?? 2}
                    onChange={(e) => update('nfse_aliquota_iss', Number(e.target.value))}
                    inputProps={{ step: 0.01, min: 0, max: 100 }} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <FormControlLabel
                    control={<Switch checked={(settings as any).nfse_iss_retido || false}
                      onChange={(e) => update('nfse_iss_retido', e.target.checked)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#14b8a6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#14b8a6' } }} />}
                    label="ISS Retido" />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#334155' }}>NFSe Nacional (Ambiente Nacional)</Typography>
                  <Chip label={(settings as any).nfse_usar_nacional ? 'Ativo' : 'Inativo'}
                    size="small" sx={{ ml: 1, bgcolor: (settings as any).nfse_usar_nacional ? '#dbeafe' : '#f1f5f9',
                    color: (settings as any).nfse_usar_nacional ? '#2563eb' : '#64748b' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                  Municípios como Belo Horizonte (MG) usam o Ambiente Nacional para NFSe. Ative esta opção se seu município utiliza o endpoint /v2/nfsen. Lembre-se de também habilitar a opção no Painel da FocusNFe.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <FormControlLabel
                      control={<Switch checked={(settings as any).nfse_usar_nacional || false}
                        onChange={(e) => update('nfse_usar_nacional', e.target.checked)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2563eb' } }} />}
                      label="Usar NFSe Nacional" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Cód. Tributação Nacional ISS"
                      value={(settings as any).nfse_codigo_tributacao_nacional || ''}
                      onChange={(e) => update('nfse_codigo_tributacao_nacional', e.target.value)}
                      disabled={!(settings as any).nfse_usar_nacional}
                      helperText="Ex: 010701" />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth size="small" label="Cód. Município Prestação"
                      value={(settings as any).nfse_codigo_municipio_prestacao || ''}
                      onChange={(e) => update('nfse_codigo_municipio_prestacao', e.target.value)}
                      disabled={!(settings as any).nfse_usar_nacional}
                      helperText="IBGE (se diferente)" />
                  </Grid>
                </Grid>
              </Box>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save button bottom */}
      <Box sx={{ mt: 3, textAlign: 'right' }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={handleSave} disabled={saving} size="large"
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}>
          Salvar Configurações
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClinicSettings;
